import React, { useEffect, useState, useCallback } from "react";
import Shell from "@/components/app/Shell";
import RequestCard from "@/components/app/RequestCard";
import NewRequestDialog from "@/components/app/NewRequestDialog";
import RatingDialog from "@/components/app/RatingDialog";
import PmocHistoryDialog from "@/components/app/PmocHistoryDialog";
import LoyaltyTab from "@/components/app/LoyaltyTab";
import ContractOverview from "@/components/app/ContractOverview";
import MobileTabBar from "@/components/app/MobileTabBar";
import ProfileEditDialog from "@/components/app/ProfileEditDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PAYMENT_METHODS_CONTRACT } from "@/lib/status";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/apiClient";
import {
  Plus, ClipboardList, Star, XCircle, Wind, MapPin, ShieldCheck, Wrench, Package, ClipboardCheck, Home, FileText, Headphones, User,
} from "lucide-react";
import { toast } from "sonner";

function ContractActions({ request, onChanged, onRate }) {
  const cancel = async () => {
    try {
      await api.patch(`/requests/${request.id}/cancel`, { reason: "Cancelado pelo cliente" });
      toast.success("Chamado cancelado.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  const canCancel = ["aberto", "atribuido"].includes(request.status);
  const canRate = request.status === "concluido" && !request.rating;
  if (!canCancel && !canRate) return null;
  return (
    <div className="flex gap-2 flex-wrap">
      {canRate && (
        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 gap-1.5"
          onClick={() => onRate(request)} data-testid={`rate-button-${request.id}`}>
          <Star className="h-4 w-4" /> Avaliar atendimento
        </Button>
      )}
      {canCancel && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
              data-testid={`cancel-button-${request.id}`}>
              <XCircle className="h-4 w-4" /> Cancelar
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancelar chamado?</AlertDialogTitle>
              <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Voltar</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={cancel} data-testid="cancel-dialog-yes">Sim, cancelar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

export default function ContractCustomerDashboard() {
  const { user } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lockedEquipment, setLockedEquipment] = useState(null);
  const [ratingFor, setRatingFor] = useState(null);
  const [pmocEquip, setPmocEquip] = useState(null);
  const [tab, setTab] = useState("visao");
  const [profileOpen, setProfileOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eq, rq] = await Promise.all([api.get("/my/equipment"), api.get("/requests")]);
      setEquipment(eq.data);
      setRequests(rq.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openForEquipment = (eq) => { setLockedEquipment(eq); setDialogOpen(true); };
  const openGeneral = () => { setLockedEquipment(null); setDialogOpen(true); };

  return (
    <Shell title="Meu contrato" subtitle="Seus equipamentos e chamados"
      actions={
        <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={openGeneral} data-testid="contract-new-request-button">
          <Plus className="h-4 w-4" /> Novo chamado
        </Button>
      }>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="visao" data-testid="ct-tab-visao">Meu contrato</TabsTrigger>
          <TabsTrigger value="equipamentos" data-testid="ct-tab-equipamentos">Meus equipamentos</TabsTrigger>
          <TabsTrigger value="chamados" data-testid="ct-tab-chamados">Meus chamados</TabsTrigger>
          <TabsTrigger value="fidelidade" data-testid="ct-tab-fidelidade">Fidelidade</TabsTrigger>
        </TabsList>

        <TabsContent value="visao">
          <ContractOverview equipment={equipment} onNav={setTab} />
        </TabsContent>

        <TabsContent value="equipamentos">
          {loading ? (
            <p className="text-slate-400 text-sm">Carregando...</p>
          ) : equipment.length === 0 ? (
            <div className="text-center py-16" data-testid="ct-empty-equipment">
              <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nenhum equipamento cadastrado no seu contrato ainda.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="ct-equipment-list">
              {equipment.map((eq) => (
                <Card key={eq.id} className="border-slate-200" data-testid={`ct-equipment-${eq.id}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="h-11 w-11 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Wind className="h-5 w-5 text-[#1e7be0]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800">
                          {eq.equipment_type}{eq.brand ? ` · ${eq.brand}` : ""}{eq.model ? ` ${eq.model}` : ""}
                        </p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />{eq.location || "-"}{eq.capacity_btu ? ` · ${eq.capacity_btu} BTU` : ""}
                        </p>
                      </div>
                    </div>
                    {eq.warranty?.type && (
                      <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-100 rounded-md px-2 py-1.5">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Garantia {eq.warranty.type === "fabrica" ? "de fábrica" : "de serviço"}
                        {eq.warranty.end ? ` · válida até ${eq.warranty.end}` : ""}
                      </div>
                    )}
                    {eq.last_maintenance_date && (
                      <p className="text-xs text-slate-400">Última manutenção: {eq.last_maintenance_date}</p>
                    )}
                    <Button size="sm" variant="outline" className="w-full gap-1.5 border-[#1e7be0] text-[#1e7be0] hover:bg-blue-50"
                      onClick={() => openForEquipment(eq)} data-testid={`ct-open-request-${eq.id}`}>
                      <Wrench className="h-4 w-4" /> Abrir chamado para este equipamento
                    </Button>
                    <Button size="sm" variant="ghost" className="w-full gap-1.5 text-slate-600"
                      onClick={() => setPmocEquip(eq)} data-testid={`ct-pmoc-${eq.id}`}>
                      <ClipboardCheck className="h-4 w-4" /> Ver histórico de manutenção (PMOC)
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="chamados">
          {loading ? (
            <p className="text-slate-400 text-sm">Carregando...</p>
          ) : requests.length === 0 ? (
            <div className="text-center py-16" data-testid="ct-empty-requests">
              <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Você ainda não abriu nenhum chamado.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="ct-requests-list">
              {requests.map((r) => (
                <RequestCard key={r.id} request={r} canPayOnline customerView paymentOptions={PAYMENT_METHODS_CONTRACT} onChanged={load}
                  actions={<ContractActions request={r} onChanged={load} onRate={setRatingFor} />} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="fidelidade">
          <LoyaltyTab />
        </TabsContent>
      </Tabs>

      <NewRequestDialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setLockedEquipment(null); }}
        onCreated={load} lockedEquipment={lockedEquipment} />
      <RatingDialog open={!!ratingFor} onOpenChange={(o) => !o && setRatingFor(null)}
        request={ratingFor || {}} onRated={load} />
      <PmocHistoryDialog open={!!pmocEquip} onOpenChange={(o) => !o && setPmocEquip(null)}
        equipmentId={pmocEquip?.id}
        equipmentLabel={pmocEquip ? `${pmocEquip.equipment_type}${pmocEquip.brand ? ` · ${pmocEquip.brand}` : ""}${pmocEquip.location ? ` — ${pmocEquip.location}` : ""}` : ""} />

      <ProfileEditDialog open={profileOpen} onOpenChange={setProfileOpen} />

      <MobileTabBar items={[
        { key: "visao", label: "Início", icon: Home, onClick: () => setTab("visao"), active: tab === "visao" },
        { key: "chamados", label: "Chamados", icon: ClipboardList, onClick: () => setTab("chamados"), active: tab === "chamados" },
        { type: "fab", label: "Solicitar", icon: Plus, onClick: openGeneral },
        { key: "contratos", label: "Contratos", icon: FileText, onClick: () => setTab("visao"), active: tab === "visao" },
        { type: "menu", key: "perfil", label: "Perfil", icon: User, links: [
          { key: "dados", label: "Meus dados", icon: User, onClick: () => setProfileOpen(true) },
          { key: "equip", label: "Meus equipamentos", icon: Wind, onClick: () => setTab("equipamentos") },
          { key: "fidelidade", label: "Fidelidade", icon: Star, onClick: () => setTab("fidelidade") },
        ] },
      ]} />
    </Shell>
  );
}
